import { CANVAS_TOKEN_NAMES } from './brandCanvas'
import { type BrandRamp, type BrandRampTokens, PRIMARY_TOKEN_NAMES } from './brandRamp'
import { SECONDARY_TOKEN_NAMES } from './brandSecondary'

export const BRAND_ATTR = 'data-ws-brand'
export const BRAND_STYLE_ID = 'ws-brand-theme'

const HEX6 = /^#[0-9a-f]{6}$/
const HEX8 = /^#[0-9a-f]{8}$/
const RGBA = /^rgba\((\d{1,3}),\s?(\d{1,3}),\s?(\d{1,3}),\s?(?:0|1|0?\.\d{1,3})\)$/
const SHADOW_LENGTHS = /^(?:0|-?\d{1,3}px)(?: (?:0|-?\d{1,3}px)){2,3} $/

const HEX8_TOKENS = new Set(['--color-primary-faint'])

const RGBA_TOKENS = new Set([
  '--color-primary-ring',
  '--color-primary-glow',
  '--color-primary-glow-strong',
  '--ai-button-glow',
  '--color-ai-glow',
])

const SHADOW_TOKENS = new Set(['--focus-ring', '--shadow-hover-primary'])

const COVERED_TOKENS: readonly string[] = [
  ...PRIMARY_TOKEN_NAMES,
  ...CANVAS_TOKEN_NAMES,
  ...SECONDARY_TOKEN_NAMES,
]

function isRgba(value: string): boolean {
  const match = RGBA.exec(value)
  if (match === null) {
    return false
  }
  for (const channel of match.slice(1, 4)) {
    if (Number(channel) > 255) {
      return false
    }
  }
  return true
}

function isBoxShadow(value: string): boolean {
  const rgbaAt = value.indexOf('rgba(')
  if (rgbaAt < 0) {
    return false
  }
  return SHADOW_LENGTHS.test(value.slice(0, rgbaAt)) && isRgba(value.slice(rgbaAt))
}

function isValidTokenValue(name: string, value: string): boolean {
  if (SHADOW_TOKENS.has(name)) {
    return isBoxShadow(value)
  }
  if (RGBA_TOKENS.has(name)) {
    return isRgba(value)
  }
  if (HEX8_TOKENS.has(name)) {
    return HEX8.test(value)
  }
  return HEX6.test(value)
}

function declarationBlock(tokens: BrandRampTokens): string | null {
  if (Object.keys(tokens).length !== COVERED_TOKENS.length) {
    return null
  }
  const lines: string[] = []
  for (const name of COVERED_TOKENS) {
    const value = tokens[name]
    if (value === undefined || !isValidTokenValue(name, value)) {
      return null
    }
    lines.push(`  ${name}: ${value};`)
  }
  return lines.join('\n')
}

export function brandRampCss(ramp: BrandRamp): string | null {
  const light = declarationBlock(ramp.light)
  const dark = declarationBlock(ramp.dark)
  if (light === null || dark === null) {
    return null
  }
  return [
    `:root[${BRAND_ATTR}]:not([data-theme="dark"]) {\n${light}\n}`,
    `:root[${BRAND_ATTR}][data-theme="dark"] {\n${dark}\n}`,
    `:root[${BRAND_ATTR}] [data-theme="dark"] {\n${dark}\n}`,
  ].join('\n\n')
}
